<template>
<div id="form-container" ref="formContainer">
    <div id="pgTitle" tabindex="-1"><h1>{{pageTitle}}</h1></div>

    <form-wizard :stepSize="stepSize"
                 :nextButtonText="buttons.next"
                 :backButtonText="buttons.back"
                 :finishButtonText="buttons.submit"
                 color="#003865"
                 :layout="layout"
                 @on-complete="onSubmit"
                 id="wizard"
                 @on-change="switchTabs"
    >
        <span slot="title" tabindex="-1"/>

        <tab-content  :title="breadcrumbs.instruct" :before-change="()=>validateStep('inst')" :after-change="()=>scrollTo()">
            <Instructions ref="inst"  @on-validate="mergePartialModels" :after-change="()=>scrollTo()" :application="technicianApplication"/>
        </tab-content>

        <tab-content :title="breadcrumbs.appInfo"
                     :before-change="()=>validateStep('appl')" :after-change="()=>scrollTo()">
            <ApplicantInfo ref="appl" @on-validate="mergePartialModels" :application="technicianApplication"
                v-bind:profile="profile"/>
        </tab-content>
        
        <tab-content :title="breadcrumbs.employ"
                     :before-change="()=>validateStep('emp')" :after-change="()=>scrollTo()">
            <Employment ref="emp" @on-validate="mergePartialModels" :technicianApplication="technicianApplication"/>
        </tab-content>

        <tab-content :title="breadcrumbs.question"
                     :before-change="()=>validateStep('ques')" :after-change="()=>scrollTo()">
            <Question ref="ques" @on-validate="mergePartialModels" :technicianApplication="technicianApplication"/>
        </tab-content>

        <tab-content :title="breadcrumbs.attach"
                     :before-change="()=>validateStep('att')" :after-change="()=>scrollTo()">
            <Attachments ref="att" @on-validate="mergePartialModels" :app="technicianApplication" v-bind:current="technicianApplication.attachments"/>
        </tab-content>

        <tab-content :title="breadcrumbs.addl"
                     :before-change="()=>validateStep('addl')" :after-change="()=>scrollTo()">
            <AddlInfo ref="addl" @on-validate="mergePartialModels" :application="technicianApplication"/>
        </tab-content>

        <tab-content :title="breadcrumbs.sig"
                     :before-change="()=>validateStep('sig')" :after-change="()=>scrollTo()">
            <Signature ref="sig"/>
        </tab-content>
        
        <tab-content :title="breadcrumbs.pay"
                     :before-change="()=>validateStep('pay')" :after-change="()=>scrollTo()">
            <Payment ref="pay" :application="technicianApplication" @on-validate="mergeCheck"/>
        </tab-content>
        
    </form-wizard>
</div>

</template>

<script>
import axios from "axios"
import { v4 as uuidv4 } from 'uuid'
import moment from 'moment'
import { commonOptions } from '@/mixins/commonOptions.js'
import { buttons, breadcrumbs } from '@/mixins/constants.js'
import Instructions from '@/components/wizard/Instructions.vue'
import ApplicantInfo from '@/components/wizard/ApplicantInfo.vue'
import Employment from '@/components/wizard/Employment.vue'
import Question from '@/components/wizard/Question.vue'
import Attachments from '@/components/wizard/Attachments.vue'
import AddlInfo from '@/components/wizard/AddlInfo.vue'
import Signature from '@/components/wizard/Signature.vue'
import Payment from '@/components/wizard/Payment.vue'
import axiosRetry from 'axios-retry'

export default {
    components: {
        Instructions,
        ApplicantInfo,
        Employment,
        Question,
        Attachments,
        AddlInfo,
        Signature,
        Payment
    },
    mixins: [
        commonOptions,
        breadcrumbs,
        buttons
    ],
    data() {
        return {
            draftId: '',
            stepSize: 'sm',
            layout: '',
            isSubmitted: false,
            shortCode: '',
            pageTitle: '',
            technicianApplication: {
                id: null,
                address: [],
                questionResponses: [],
                prevContacts: []
            },
            profile: { },
            isDisabled: false,
            isCheck: false
        }
    },
    created() {
        document.title = 'BodyArt - Application to Reclassify from Temporary to Full'
        this.pageTitle = document.title

        // check to see if a draft id was sent in the query parameters
        if (this.$route.params.draftId) {
            this.loadDraft(this.$route.params.draftId)
            window.scrollTo(0,0)
        } else {
            this.loadTemp(this.$route.params.id)
        }

        this.loadProfile()
    },
    
    computed: {
        appTypeLabel() {
            if (this.technicianApplication.subCategory === 'FULL') {
                return "Initial Technician Application"
            }
            return ""
        }
    },

    methods: {
        buttons: function() {
            return buttons
        },
        breadcrumbs: function() {
            return breadcrumbs
        },
        onSubmit() {
            if (!this.isDisabled) {
                this.isDisabled = true
                if (this.technicianApplication.questions) {
                    delete this.technicianApplication.questions
                }
                if (this.technicianApplication.otherName !== 'Y') {
                    this.technicianApplication.otherNames = []
                }
                if (this.technicianApplication.tradeName !== 'Y') {
                    this.technicianApplication.tradeNames = []
                }
                if (this.technicianApplication.subCategory === 'FULL' ) {
                    this.technicianApplication.mnSupervisor = []
                } else {
                    this.technicianApplication.employers = []
                }
                if (this.technicianApplication.subCategory !== 'GUEST') {
                    this.technicianApplication.mnSupervisor.forEach(s => s.workDate = null)
                }
                this.sendToServer()
            }
        },
        sendToServer() {
            axiosRetry(axios, { retries: 0 })
            axios.post(`${process.env.VUE_APP_API_BASE_URL}/application`, this.technicianApplication)
                .then(response => {
                    this.deleteDraft()
                    this.isSubmitted = true
                    if (!this.isCheck) {
                        // redirect to usbank for payment
                        this.sendToUSBank(response.data)
                    } else {
                        this.sendToCheckInstructions()
                    }
            })
        },

        loadProfile() {
            axiosRetry(axios, { retries: 3,
                retryDelay: axiosRetry.exponentialDelay  })

            axios.get(`${process.env.VUE_APP_API_BASE_URL}/profile`)
                .then(response => { 
                    this.profile = response.data
            })
        },

        sendToUSBank(formData) {
            var usbForm = document.createElement('form')
            usbForm.method = 'post' 
            usbForm.action = process.env.VUE_APP_USBANK_ENDPOINT
            for (var k in formData) {
                var input = document.createElement('input')
                input.setAttribute('name', k)
                input.setAttribute('value', formData[k])
                usbForm.appendChild(input) ;
            }
            document.body.appendChild(usbForm)
            usbForm.submit()
            document.body.removeChild(usbForm) ;
        },

        sendToCheckInstructions() {
            this.$router.push('/checkInstructions')
        },
        
        validateStep(name) {
            var refToValidate = this.$refs[name]
            return refToValidate.validate()
        },

        mergeCheck(model) {
            this.isCheck = model['isCheck']
        },

        mergePartialModels(model, isValid) {
            if(isValid){
                // merging each step model into the final model
                this.technicianApplication = Object.assign({}, this.technicianApplication, model)
                if (this.technicianApplication.opts) {
                    delete this.technicianApplication.opts
                }
                this.saveDraft()
            }
        },

        scrollTo() {
            let elem = document.getElementById("wizard")
            elem.scrollIntoView()
        },

        focus(e) {
            let elem = document.getElementsByClassName("card-header")[e+1]
            elem.focus()
        },

        switchTabs(prevIdx, nextIdx) {
            var title = 'Welcome'
            switch (nextIdx) {
                case 0:
                    title = 'Instructions'
                    break;
                case 1:
                    title = 'Applicant Information'
                    break;
                case 2:
                    title = 'Employment Information'
                    break;
                case 3:
                    title = 'Questions'
                    break;
                case 4:
                    title = 'Attachments'
                    break;
                case 5:
                    title = 'Additional Information'
                    break;
                case 6:
                    title = 'Signature'
                    break;
                case 7:
                    title = 'Payment'
                    break;
                default:
                    title = 'Application'
            }
            document.title = title + ' - Reclassification Application - ' + this.appTypeLabel
            this.pageTitle = document.title
            document.getElementById('pgTitle').focus()
        },
        
        removeTabFocus() {
            this.$nextTick(function () {
                var steps = document.querySelectorAll("div[role='tab']")
                steps.forEach(step => {
                    step.setAttribute("tabindex", "-1")
                })

                var steps2 = document.querySelectorAll(".wizard-nav-pills>li>a");
                steps2.forEach(step => {
                    step.setAttribute("tabindex", "-1")
                    step.setAttribute("aria-disabled","true")
                })
            })
        },

        deleteDraft() {
            if (this.draftId) {
                axios.delete(`${process.env.VUE_APP_API_BASE_URL}/drafts/${this.draftId}`)
                .then(response => {
                    console.log('deleted draft ' + this.draftId)
                })
            }
        },

        saveDraft() {
            if (!this.draftId) {
                this.draftId = uuidv4()
            }

            var currentApp = JSON.parse(JSON.stringify(this.technicianApplication))
            if (currentApp.questions) {
                delete currentApp.questions
            }
            if (currentApp.otherName !== 'Y') {
                currentApp.otherNames = []
            }
            if (currentApp.tradeName !== 'Y') {
                currentApp.tradeNames = []
            }
            if (currentApp.subCategory === 'FULL' ) {
                currentApp.mnSupervisor = []
            }
            var draft = {
                id: this.draftId,
                payload: JSON.stringify(currentApp)
            }

            axios.post(`${process.env.VUE_APP_API_BASE_URL}/drafts/save`, draft)
                .then(response => { 
                    if (response.data) {
                        console.log('saved draft ' + draft.id)
                    }
            })

        },

        loadDraft(id) {
            axios.get(`${process.env.VUE_APP_API_BASE_URL}/drafts/${id}`)
                .then(response => {
                    if (response.data) {
                        this.technicianApplication = response.data.payload
                        this.draftId = id
                        this.$set(this,"technicianApplication",this.technicianApplication)
                        this.loadQuestions()
                    }
            })
        },

         loadQuestions() {
                axios.get(`${process.env.VUE_APP_API_BASE_URL}/Questions/public`, { ignoreHeader: true })
                    .then(response => {
                        this.questions = response.data
                        for (let q of this.questions) {
                            this.questionResponses.push({
                                response: null,
                                additionalResponse: null,
                                question: q.code,
                                application: null
                            })
                        }

                        for (let q of this.technicianApplication.questionResponses) {
                            for (let q2 of this.questionResponses) {
                                if (q.question === q2.question) {
                                    q2.response = q.response
                                    q2.additionalResponse = q.additionalResponse
                                }
                            }
                        }
                    })
            },
            loadTemp(id) {
                axios.get(`${process.env.VUE_APP_API_BASE_URL}/renewal/reclassify/${id}`)
                    .then(response => {
                        var existing = response.data
                        for (var i = 0; i < existing.address.length; i++) {
                            existing.address[i].addressType = existing.address[i].addressType.code
                            this.technicianApplication.address.push(existing.address[i])
                        }
                        this.technicianApplication.reclassify = existing.licenseNbr
                        this.technicianApplication.subCategory = 'FULL'
                        this.technicianApplication.category = 'TECHNICIAN'
                        this.technicianApplication.preferredPhone = existing.preferredPhone
                        this.technicianApplication.emailAddress = existing.emailAddress
                        this.technicianApplication.otherNames = existing.otherNames
                        this.technicianApplication.tradeNames = existing.tradeNames
                        this.technicianApplication.licenseType = existing.licenseType.code
                        this.technicianApplication.applyMethod = existing.applyMethod.code
                        this.$set(this,"technicianApplication",this.technicianApplication)
                    })
                    .catch(err => { console.log("ERROR: " + err) })
            },

    },
    updated: function() {
        this.removeTabFocus()
    },
    mounted() {
        this.removeTabFocus()
    },
}
</script>

<style>
.card {
    margin-bottom: 18px;
}
.card-header {
    font-weight: 700;
}
</style>
